'use client';

import React, { useState, useEffect } from 'react';
import { formatAddress } from '@/utils/formatAddress';
import VerticalTable from '../VerticalTable';
import { formatLanguageName } from '@/utils/languages';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShare } from '@fortawesome/pro-duotone-svg-icons';
import { RoundedSectionCard } from '@/components/common/RoundedSectionCard';
import { useHasTenderAccess } from '@/hooks/useHasTenderAccess';
import { useAuthenticated } from '@/hooks/useAuthenticated';
import TenderAccessButton from '../access/TenderAccessButton';
import { SubmissionType } from '@/actions/dtos/tender/submission';
import { TenderDetails } from '@/actions/dtos/tender/tender';

interface SubmissionDetailsSectionProps {
  tenderId: string;
  tenderDetails: TenderDetails;
  isActiveTender: boolean;
}

const SubmissionDetailsSection: React.FC<SubmissionDetailsSectionProps> = ({
  tenderId,
  tenderDetails,
  isActiveTender,
}) => {
  const { isAuthenticated, isLoading: isAuthenticationLoading } = useAuthenticated();
  const { hasAccessPermission, isLoading: isTenderAccessLoading } = useHasTenderAccess(tenderId);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showAccessButton, setShowAccessButton] = useState(isActiveTender && !isLoading);

  useEffect(() => {
    setShowAccessButton(isActiveTender && !isLoading);
  }, [isActiveTender, isLoading]);

  useEffect(() => {
    setIsLoading(isTenderAccessLoading || isAuthenticationLoading);
  }, [isAuthenticationLoading, isTenderAccessLoading]);

  const details = [
    {
      label: 'Abgabeform:',
      data:
        tenderDetails.submission.allowedModes.length > 0
          ? tenderDetails.submission.allowedModes
              .map((mode) => SubmissionType[mode as unknown as keyof typeof SubmissionType])
              .join(', ')
          : null,
    },
    {
      label: 'Angebotssprachen:',
      data:
        tenderDetails.submission.languages.length > 0
          ? tenderDetails.submission.languages.map((language) => formatLanguageName(language)).join(', ')
          : null,
    },
    {
      label: 'Abgabe-Adresse:',
      data: formatAddress(tenderDetails.submission.address),
    },
    {
      label: 'Anzahl der Angebote:',
      data:
        tenderDetails.submission.multipleSubmissionsAllowed === null &&
        tenderDetails.submission.alternativeSubmissionsAllowed === null ? null : (
          <div className="flex flex-col">
            <span>
              {tenderDetails.submission.multipleSubmissionsAllowed === null
                ? null
                : tenderDetails.submission.multipleSubmissionsAllowed
                  ? 'Mehrere Hauptangebote sind zulässig.'
                  : 'Mehrere Hauptangebote sind nicht zulässig.'}
            </span>
            <span>
              {tenderDetails.submission.alternativeSubmissionsAllowed === null
                ? null
                : tenderDetails.submission.alternativeSubmissionsAllowed
                  ? 'Nebenangebote sind zulässig.'
                  : 'Nebenangebote sind nicht zulässig.'}
            </span>
          </div>
        ),
    },
  ];

  return (
    <RoundedSectionCard>
      <VerticalTable
        title={
          <div className="flex gap-3">
            <span className="flex h-7 w-6 items-center justify-center">
              <FontAwesomeIcon className="fa-fw h-6 w-6" icon={faShare} />
            </span>
            <h2 className="text-xl font-light">ABGABE</h2>
          </div>
        }
        button={
          <>
            {showAccessButton && (
              <TenderAccessButton
                tenderId={tenderId}
                isAuthenticated={isAuthenticated}
                hasAccess={hasAccessPermission}
                label={'Zur Angebotsabgabe'}
              />
            )}
          </>
        }
        details={details}
      />
    </RoundedSectionCard>
  );
};

export default SubmissionDetailsSection;
